/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGetAccountMyPage } from '#api/accounts';
import { PATH } from '#const/path';
import useMyAccountQuery from '#hooks/queries/accounts/useMyAccountQuery';
import useGymAdminsScheduleDayQuery from '#hooks/queries/gymAdmin/useGymAdminsScheduleDayQuery';
import useGymAdminsScheduleWeekQuery from '#hooks/queries/gymAdmin/useGymAdminsScheduleWeekQuery';
import { AdminReservedMember, ReservedPersonalSchedule } from '#types/schedule';
import getFirstDayOfWeek from '#utils/getFirstDayOfWeek';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import Loading from 'src/components/molecules/Loading';
import EmptyCoachMark from '../WeeklyCalendar/EmptyCoachMark';
import LectureCoachMark from '../WeeklyCalendar/LectureCoachMark';
import { selectedCoachIdAtom } from '../atoms/coach.atoms';
import CalendarHeader from '../components/CalendarHeader';
import CardMemoWrapper from '../components/CardMemoWrapper';
import ChipButtons from '../components/ChipButtons';
import CurrentIndicator from '../components/CurrentIndicator';
import DatePicker from '../components/DatePicker';
import HeaderWeekDay from '../components/HeaderWeekDay';
import Timeline from '../components/Timeline';
import * as CommonS from '../styles';
import { CalendarPeriodic } from '../types';
import { useChipOptions, useColumnsDaily, useColumnsWeekly } from './hooks';
import * as S from './styles';

export default function WeeklyCalendarForAdmin() {
  const { data: userInfo } = useGetAccountMyPage();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const { data: account } = useMyAccountQuery();
  const [searchParams] = useSearchParams();
  const startAtParam = searchParams.get('startAt');
  const periodicParam = searchParams.get('periodic') as CalendarPeriodic;
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [calendarPeriodic, setCalendarPeriodic] = useState<CalendarPeriodic>(
    periodicParam ?? 'WEEKLY'
  );
  const [activeDate, setActiveDate] = useState<dayjs.Dayjs>();
  const [selectedCoachId, setSelectedCoachId] =
    useRecoilState(selectedCoachIdAtom);
  const [isStickyDailyHeader, setIsStickyDailyHeader] =
    useState<boolean>(false);
  const [dailyHeaderOffset, setDailyHeaderOffset] = useState<number>(0);

  const { data: weeklySchedule, isLoading: isLoadingWeeklySchedule } =
    useGymAdminsScheduleWeekQuery({
      startAt: activeDate ? activeDate?.format('YYYY-MM-DD') : '',
      enabled: calendarPeriodic === 'WEEKLY'
    });

  const { data: dailySchedule, isLoading: isLoadingDailySchedule } =
    useGymAdminsScheduleDayQuery({
      startAt: activeDate ? activeDate?.format('YYYY-MM-DD') : '',
      enabled: calendarPeriodic === 'DAILY'
    });

  const isLoading = useMemo(() => {
    return (
      (calendarPeriodic === 'WEEKLY' && isLoadingWeeklySchedule) ||
      (calendarPeriodic === 'DAILY' && isLoadingDailySchedule)
    );
  }, [calendarPeriodic, isLoadingWeeklySchedule, isLoadingDailySchedule]);

  const chipOptions = useChipOptions(weeklySchedule);
  const columnsWeekly = useColumnsWeekly({
    data: weeklySchedule,
    calendarPeriodic,
    coachId: selectedCoachId,
    activeDate
  });
  const columnsDaily = useColumnsDaily({
    data: dailySchedule,
    calendarPeriodic,
    activeDate
  });

  const handleChangeDate = (date: dayjs.Dayjs) => {
    const queryParams = new URLSearchParams(search);
    queryParams.set('startAt', date.format('YYYY-MM-DD'));
    navigate(`${pathname}?${queryParams.toString()}`);
  };

  useEffect(() => {
    if (!weeklySchedule) {
      return;
    }

    const activeObj = weeklySchedule[0];
    const coachId = activeObj?.coachId;
    setSelectedCoachId(c => (c ? c : coachId));
  }, [weeklySchedule]);

  useEffect(() => {
    return () => {
      setSelectedCoachId(undefined);
    };
  }, []);

  useEffect(() => {
    if (calendarPeriodic === 'WEEKLY') {
      setActiveDate(
        getFirstDayOfWeek(startAtParam ? dayjs(startAtParam) : dayjs())
      );
    } else {
      setActiveDate(startAtParam ? dayjs(startAtParam) : dayjs());
    }
  }, [startAtParam]);

  useEffect(() => {
    const handler = debounce(() => {
      const headerOffset = stickyHeaderRef.current?.getBoundingClientRect()
        .top as number;
      const offset = scrollableRef.current?.getBoundingClientRect()
        .top as number;
      const y = Math.abs(headerOffset - offset);

      if (headerOffset <= 0) {
        setIsStickyDailyHeader(true);
        setDailyHeaderOffset(Math.abs(headerOffset));
      } else {
        setIsStickyDailyHeader(false);
        setDailyHeaderOffset(0);
      }
    }, 20);

    if (calendarPeriodic === 'DAILY') {
      window.addEventListener('scroll', handler);
    } else {
      window.removeEventListener('scroll', handler);
    }

    () => window.removeEventListener('scroll', handler);
  }, [calendarPeriodic]);

  return (
    <CommonS.Root>
      <EmptyCoachMark />

      <LectureCoachMark />

      {isLoading && (
        <CommonS.LoadingWrapper>
          <Loading />
        </CommonS.LoadingWrapper>
      )}
      <div ref={stickyHeaderRef} />
      <div style={{ padding: '20px 20px 0' }}>
        <CalendarHeader
          activeDate={activeDate}
          defaultCalendarPeriodic={calendarPeriodic}
          onChangePeriodic={value => {
            setCalendarPeriodic(value);
            const queryParams = new URLSearchParams(search);
            queryParams.set('periodic', value);
            navigate(`${pathname}?${queryParams.toString()}`);
          }}
        />
      </div>
      <CommonS.StickyHeader style={{ paddingTop: '10px' }}>
        <CardMemoWrapper
          calendarPeriodic={calendarPeriodic}
          date={activeDate}
        />
        <DatePicker
          calendarPeriodic={calendarPeriodic}
          date={activeDate}
          onChange={handleChangeDate}
        />
        {chipOptions && calendarPeriodic === 'WEEKLY' && (
          <ChipButtons
            selectedValue={selectedCoachId}
            options={chipOptions}
            onChange={value => setSelectedCoachId(value)}
          />
        )}
        {calendarPeriodic === 'WEEKLY' && (
          <HeaderWeekDay columns={columnsWeekly} />
        )}
      </CommonS.StickyHeader>
      <S.TimelineWrapper ref={scrollableRef}>
        <CurrentIndicator />
        <S.ContainerScrollable
          isDaily={calendarPeriodic === 'DAILY'}
          disableScrollable={calendarPeriodic === 'WEEKLY'}
        >
          {calendarPeriodic === 'DAILY' && (
            <S.HeaderWeekDayWrapper
              isSticky={isStickyDailyHeader}
              $y={dailyHeaderOffset}
            >
              <HeaderWeekDay columns={columnsDaily} />
            </S.HeaderWeekDayWrapper>
          )}

          <Timeline
            columns={
              calendarPeriodic === 'WEEKLY' ? columnsWeekly : columnsDaily
            }
            activeDate={activeDate as dayjs.Dayjs}
            onClickSchedule={data => {
              if ((data as ReservedPersonalSchedule).isPersonalSchedule) {
                if (
                  (data as ReservedPersonalSchedule)?.coachName ===
                  userInfo?.name
                ) {
                  navigate(
                    `${PATH.CALENDER_PERSONAL_DETAIL}/${(
                      data as ReservedPersonalSchedule
                    ).personalScheduleId!}`,
                    {
                      state: {
                        coachId: selectedCoachId
                      }
                    }
                  );
                } else {
                  navigate(
                    `${PATH.CALENDER_PERSONAL_DETAIL}/${(
                      data as ReservedPersonalSchedule
                    ).personalScheduleId!}?isAdmin=Y`,
                    {
                      state: {
                        coachId: selectedCoachId
                      }
                    }
                  );
                }

                return;
              }

              if ((data as AdminReservedMember)?.coachId === account?.id) {
                navigate(
                  `${PATH.CALENDER_DETAIL}/${
                    (data as AdminReservedMember).registeredLectureId
                  }`,
                  {
                    state: {
                      coachId: selectedCoachId
                    }
                  }
                );
              } else {
                navigate(
                  `${PATH.CALENDER_DETAIL}/${
                    (data as AdminReservedMember).registeredLectureId
                  }/admin`,
                  {
                    state: {
                      coachId: selectedCoachId
                    }
                  }
                );
              }
            }}
          />
        </S.ContainerScrollable>
      </S.TimelineWrapper>
    </CommonS.Root>
  );
}
